// ✅ VIEW-ONLY VERSION (No Edit / No Save / No Photo Upload)
// - Same “ReportStudentMarks” theme
// - Keeps: filters -> student list -> click row -> drill-down profile cards
// - Keeps: optional search autocomplete
// - Keeps: passport photo preview (view only)

import React, { useEffect, useState, memo, useMemo } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Autocomplete,
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  useMediaQuery,
  LinearProgress,
  Alert,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { API_URL, API_UPLOAD_URL } from "./config";

// ---- Simple display-only field ----
const DisplayField = memo(function DisplayField({ label, value }) {
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
});
// -----------------------------------

const ReportStudentPersonalInformation = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const COLS_XL = 6;

  // ✅ Theme constants (match ReportStudentMarks)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: isSmDown ? "flex-start" : "center",
    justifyContent: "space-between",
    gap: 2,
    flexDirection: isSmDown ? "column" : "row",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(25,118,210,0.45)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(240,120,40,0.80)",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const tableWrapSx = { ...surfaceSx, overflow: "hidden" };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 0.8 },
  });

  // ---------- State ----------
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [studentList, setStudentList] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [shouldSearch, setShouldSearch] = useState(false);
  const [logincampusCode, setCampusCode] = useState("");
  const [passportPreview, setPassportPreview] = useState(null);
  const [originalPhotoFilename, setOriginalPhotoFilename] = useState(null);

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [allFilteredStudents, setAllFilteredStudents] = useState([]);

  const token = localStorage.getItem("auth");

  // dropdown filters and options
  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [loadingLookups, setLoadingLookups] = useState(false);

  // students list based on dropdowns
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Get campus code from token
  useEffect(() => {
    if (!token) return;
    try {
      const decodedToken = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decodedToken.campusCode || decodedToken.CampusCode || "");
    } catch (err) {
      console.error("Error decoding token:", err);
    }
  }, [token]);

  // fetch dropdown lookups
  useEffect(() => {
    if (!logincampusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: logincampusCode },
        });
        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error("Failed to load dropdowns:", e);
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logincampusCode, authHeader]);

  // Update passport preview when selectedStudent changes (view-only)
  useEffect(() => {
    if (selectedStudent?.PassportPhoto) {
      const url = `${API_UPLOAD_URL}/uploads/${selectedStudent.PassportPhoto}`;
      setPassportPreview(url);
      setOriginalPhotoFilename(selectedStudent.PassportPhoto);
    } else {
      setPassportPreview(null);
      setOriginalPhotoFilename(null);
    }
  }, [selectedStudent]);

  // Fetch student list for auto-complete search (simple debounce)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (searchTerm.length < 3 || !logincampusCode) {
        setStudentList([]);
        return;
      }
      try {
        const response = await axios.get(
          `${API_URL}/api/studentList?search=${encodeURIComponent(
            searchTerm
          )}&CampusCode=${encodeURIComponent(logincampusCode)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStudentList(response.data || []);
      } catch (error) {
        console.error("Error fetching student list:", error);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm, logincampusCode, token]);

  // Fetch detailed report data (drill-down)
  useEffect(() => {
    const fetchData = async () => {
      if (!shouldSearch || !selectedStudent || !logincampusCode) {
        setData([]);
        return;
      }

      try {
        const params = {
          search: selectedStudent.StudentID,
          CampusCode: logincampusCode,
        };

        if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
        if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
        if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
        if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

        const response = await axios.get(
          `${API_URL}/api/dataReportStudentPersonalInformationUpdate`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params,
          }
        );

        if (Array.isArray(response.data) && response.data.length > 0) {
          setData(response.data);

          // update preview from returned item as well (view-only)
          if (response.data[0]?.PassportPhoto) {
            setOriginalPhotoFilename(response.data[0].PassportPhoto);
            setPassportPreview(
              `${API_UPLOAD_URL}/uploads/${response.data[0].PassportPhoto}`
            );
          } else {
            setOriginalPhotoFilename(null);
            setPassportPreview(null);
          }
        } else {
          setData([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 401) navigate("/");
      }
    };
    fetchData();
  }, [shouldSearch, selectedStudent, logincampusCode, filters, token, navigate]);

  const getImageUrl = (value) => {
    if (!value) return null;

    if (
      typeof value === "string" &&
      (value.startsWith("blob:") ||
        value.startsWith("http://") ||
        value.startsWith("https://"))
    ) {
      return value;
    }

    return `${API_UPLOAD_URL}/uploads/${value}`;
  };

  const anyFilterSelected =
    (filters.SubCampusCode?.length || 0) > 0 ||
    (filters.DegreeCode?.length || 0) > 0 ||
    (filters.BranchCode?.length || 0) > 0 ||
    (filters.SemesterCode?.length || 0) > 0;

  const handleLoadStudents = async () => {
    setListError("");
    setFilteredStudents([]);
    if (!logincampusCode) return;
    if (!anyFilterSelected) {
      setListError("Select at least one dropdown filter to load students.");
      return;
    }

    try {
      setLoadingList(true);
      const params = { CampusCode: logincampusCode };
      if (filters.SubCampusCode.length) {params.SubCampusCode = filters.SubCampusCode.join(",");}
      if (filters.DegreeCode.length) {params.DegreeCode = filters.DegreeCode.join(",");}
      if (filters.BranchCode.length) {params.BranchCode = filters.BranchCode.join(",");}
      if (filters.SemesterCode.length) {params.SemesterCode = filters.SemesterCode.join(",");}

      const { data } = await axios.get(
        `${API_URL}/api/dataReportStudentPersonalInformationList`,
        {
          ...authHeader,
          params,
        }
      );

      const arr = Array.isArray(data) ? data : [];
      setFilteredStudents(arr);
      setAllFilteredStudents(arr);
      setIsCollapsed(false);
      if (!arr.length) setListError("No students found for the selected filters.");
    } catch (e) {
      console.error("Error loading student list:", e);
      setListError("Failed to load students. Please try again.");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = () => {
    setShouldSearch(!!selectedStudent);
    if (!selectedStudent) setData([]);
  };

  // IST date (display only)
  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", {
          timeZone: "Asia/Kolkata",
        })
      : "";

  const FieldGrid = ({ children, count }) => {
    const c = count ?? COLS_XL;
    return (
      <Box
        sx={{
          display: "grid",
          gap: 2,
          alignItems: "start",
          gridAutoFlow: "row dense",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: `repeat(${Math.min(c, 4)}, 1fr)`,
            xl: `repeat(${c}, 1fr)`,
          },
        }}
      >
        {children}
      </Box>
    );
  };

  const handleStudentRowClick = (s) => {
    // toggle collapse if already collapsed to single row
    if (
      isCollapsed &&
      filteredStudents.length === 1 &&
      filteredStudents[0].StudentID === s.StudentID
    ) {
      setFilteredStudents(allFilteredStudents);
      setIsCollapsed(false);
      return;
    }

    setFilteredStudents([s]);
    setIsCollapsed(true);

    setSelectedStudent({
      StudentID: s.StudentID,
      FullName: s.FullName,
      SubCampusCode: s.SubCampusCode,
      DegreeCode: s.DegreeCode,
      BranchCode: s.BranchCode,
      SemesterCode: s.SemesterCode,
      PassportPhoto: s.PassportPhoto,
    });

    setShouldSearch(true);
  };

  const renderSectionCard = (title, fields, count, extraContent = null) => {
    return (
      <Paper key={title} variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
        <Box sx={{ pl: 1 }}>
          <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
            {title}
          </Typography>

          <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

          <FieldGrid count={count}>
            {fields.map(([label, value]) => (
              <DisplayField key={`${title}-${label}`} label={label} value={value} />
            ))}

            {extraContent &&
              (React.isValidElement(extraContent)
                ? React.cloneElement(extraContent, { key: `${title}-__extra__` })
                : extraContent)}
          </FieldGrid>
        </Box>
      </Paper>
    );
  };

  const renderChildReadOnlySection = (title, rows, fields, count) => (
    <Paper variant="outlined" sx={{ ...sectionSx, mb: 2 }}>
      <Box sx={{ pl: 1 }}>
        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
          {title}
        </Typography>

        <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

        {!rows?.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No records found.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {rows.map((row, idx) => (
              <Paper
                key={`${title}-${idx}`}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2, borderColor: BORDER }}
              >
                <FieldGrid count={count}>
                  {fields.map((f) => (
                    <DisplayField
                      key={`${title}-${idx}-${f.key}`}
                      label={f.label}
                      value={
                        f.type === "boolean"
                          ? row[f.key]
                            ? "Yes"
                            : "No"
                          : row[f.key]
                      }
                    />
                  ))}
                </FieldGrid>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Information (View Only)
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students and click a row to auto-load detailed profile
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters Section (accent stripe) */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  alignItems: "start",
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))
                  }
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                  <Button
                    variant="contained"
                    onClick={handleLoadStudents}
                    disabled={loadingList}
                    sx={{ ...primaryBtnSx, height: 36 }}
                  >
                    {loadingList ? "Loading…" : "Filter Students"}
                  </Button>

                  {listError && (
                    <Typography variant="body2" sx={{ color: "error.main" }}>
                      {listError}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>

          {loadingList && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
            </Box>
          )}

          {/* ✅ Search Section */}
          <Paper variant="outlined" sx={{ ...surfaceSx, p: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
              Search (optional)
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

            <Stack
              direction={isSmDown ? "column" : "row"}
              spacing={1}
              alignItems={isSmDown ? "stretch" : "center"}
            >
              <Autocomplete
                freeSolo
                fullWidth
                options={studentList}
                getOptionLabel={(option) =>
                  option?.FullName ? `${option.FullName} (${option.StudentID})` : ""
                }
                onInputChange={(_, v) => setSearchTerm(v)}
                onChange={(_, v) => setSelectedStudent(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Name or StudentID"
                    size="small"
                    sx={inputSx}
                  />
                )}
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ ...primaryBtnSx, height: 36, minWidth: 120 }}
              >
                Search
              </Button>
            </Stack>
          </Paper>

          {shouldSearch && data.length === 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, p: 3, mt: 2, textAlign: "center" }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                No results found
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Try selecting a student from the list or using Search.
              </Typography>
            </Paper>
          )}

          {/* ✅ Students list table */}
          {filteredStudents.length > 0 && (
            <Paper variant="outlined" sx={{ ...surfaceSx, mt: 2 }}>
              <Box sx={{ p: 2 }}>
                <Typography sx={{ mb: 1, fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  Students Matching Filters {isCollapsed ? "(collapsed)" : ""}
                </Typography>

              {/* OUTER – keeps rounded corners & spacing */}
              <Paper elevation={0} sx={tableWrapSx}>

                {/* INNER – ONLY for horizontal scroll */}
                <Box
                  sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                  }}
                >
                  <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 60 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredStudents.map((s, idx) => (
                        <TableRow
                          key={`${s.StudentID}-${idx}`}
                          sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                          hover
                          onClick={() => handleStudentRowClick(s)}
                        >
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{s.StudentID}</TableCell>
                          <TableCell>{s.FullName}</TableCell>
                          <TableCell>{s.SubCampusCode}</TableCell>
                          <TableCell>{s.DegreeCode}</TableCell>
                          <TableCell>{s.BranchCode}</TableCell>
                          <TableCell>{s.SemesterCode}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
                </Paper>
              </Box>
            </Paper>
          )}

          {/* ✅ Drill-down detail cards */}
          {shouldSearch &&
            data.map((item) => (
              <Box key={item.StudentID} sx={{ mt: 2 }}>
                {renderSectionCard(
                  "Basic Information",
                  [
                    ["Student ID", item.StudentID],
                    ["Full Name", item.FullName],
                    ["Aadhar Number", item.AadharNumber],
                    ["Admission Date", convertToISTDate(item.AdmissionDate)],
                    ["Academic Year", item.AcademicYear],
                    ["Entry", item.Entry],
                    ["Qualification", item.Qualification],
                    ["Admission Type", item.AdmissionType],
                    ["Hostel Type", item.HostelType],
                    ["Username", item.Username],
                  ],
                  6,
                  (passportPreview || originalPhotoFilename) && (
                    <Box
                      sx={{
                        gridColumn: { xs: "1 / -1", sm: "1 / -1", lg: "6" },
                        gridRow: { xs: "auto", sm: "auto", lg: "1 / span 2" },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                        alignItems: { xs: "flex-start", md: "flex-end" },
                        gap: 1,
                      }}
                    >
                      {passportPreview ? (
                        <Box
                          component="img"
                          src={passportPreview}
                          alt="Profile"
                          sx={{
                            width: 160,
                            height: 220,
                            borderRadius: 2,
                            objectFit: "cover",
                            border: `1px solid ${BORDER}`,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 160,
                            height: 220,
                            borderRadius: 2,
                            border: `1px dashed ${BORDER}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                          }}
                        >
                          No photo
                        </Box>
                      )}
                    </Box>
                  )
                )}

                {renderSectionCard(
                  "Personal Information",
                  [
                    ["Date Of Birth", convertToISTDate(item.DateOfBirth)],
                    ["Gender", item.Gender],
                    ["Blood Group", item.BloodGroup],
                    ["Religion", item.Religion],
                    ["Caste", item.Caste],
                    ["SubCaste", item.SubCaste],
                  ],
                  6
                )}

                {renderSectionCard(
                  "Contact Information",
                  [
                    ["Address Line1", item.AddressLine1],
                    ["Address Line2", item.AddressLine2],
                    ["City", item.City],
                    ["District", item.District],
                    ["State", item.State],
                    ["Zip", item.Zip],
                    ["Phone Number", item.PhoneNumber],
                    ["Alternate Phone Number", item.AlternatePhoneNumber],
                    ["Email Address", item.EmailAddress],
                  ],
                  3
                )}

                {renderSectionCard(
                  "Family Information",
                  [
                    ["Father Name", item.FatherFullName],
                    ["Father Aadhar", item.FatherAadharNumber],
                    ["Father Qualification", item.FatherQualification],
                    ["Father Occupation", item.FatherOccupation],
                    ["Father Phone Number", item.FatherPhoneNumber],
                    ["Mother Name", item.MotherFullName],
                    ["Mother Aadhar", item.MotherAadharNumber],
                    ["Mother Qualification", item.MotherQualification],
                    ["Mother Occupation", item.MotherOccupation],
                    ["Mother Phone Number", item.MotherPhoneNumber],
                  ],
                  5
                )}

                {renderSectionCard(
                  "Academic Information",
                  [
                    ["Campus Code", item.CampusCode],
                    ["SubCampus Code", item.SubCampusCode],
                    ["Degree Code", item.DegreeCode],
                    ["Branch Code", item.BranchCode],
                    ["IsStudentActive", item.IsStudentActive],
                  ],
                  5
                )}

                {renderChildReadOnlySection(
                  "Education Details",
                  item.EducationDetails || [],
                  [
                    { label: "Level", key: "EducationLevel" },
                    { label: "Board Type", key: "BoardType" },
                    { label: "Institution", key: "InstitutionName" },
                    { label: "Hall Ticket", key: "HallTicketNumber" },
                    { label: "Percentage", key: "Percentage" },
                    { label: "CGPA", key: "CGPA" },
                    { label: "City", key: "InstitutionCity" },
                    { label: "State", key: "InstitutionState" },
                    { label: "Passed Out Year", key: "PassedOutYear" },
                  ],
                  9
                )}

                {renderChildReadOnlySection(
                  "Competitive / Entrance Exams",
                  item.CompetitiveExams || [],
                  [
                    { label: "Exam Type", key: "ExamType" },
                    { label: "Hall Ticket", key: "HallTicketNumber" },
                    { label: "Rank", key: "RankNo" },
                    { label: "Score", key: "Score" },
                    { label: "Year", key: "ExamYear" },
                  ],
                  5
                )}

                {renderChildReadOnlySection(
                  "Document Information",
                  item.DocumentDetails || [],
                  [
                    { label: "Document Type", key: "DocumentType" },
                    { label: "Received", key: "IsReceived", type: "boolean" },
                    { label: "Verified", key: "IsVerified", type: "boolean" },
                    { label: "Comments", key: "Comments" },
                  ],
                  4
                )}

                {renderSectionCard(
                  "Documents Storage Information",
                  [
                    ["Stored Info", item.DocumentsStoredInfo],
                    ["Returned To", item.DocumentsReturnedTo],
                    ["Returned On", item.DocumentsReturnedOn],
                    ["Verified By", item.DocumentsReturnedBy],
                  ],
                  4
                )}

                {renderSectionCard(
                  "Reference Information",
                  [
                    ["Reference Name", item.ReferenceName],
                    ["Reference Phone Number", item.ReferencePhoneNumber],
                  ],
                  2
                )}
                
                {renderSectionCard(
                  "Guardian Information",
                  [
                    ["Guardian 1 Full Name", item.Guardian1FullName],
                    ["Guardian 2 Full Name", item.Guardian2FullName],
                    ["Guardian 3 Full Name", item.Guardian3FullName],
                  ],
                  3,
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(3, 1fr)",
                        },
                      }}
                    >
                      {[1, 2, 3].map((n) => {
                        const imgField = `Guardian${n}Image`;
                        const imgUrl = getImageUrl(item[imgField]);
                      
                        return (
                          <Box key={imgField}>
                            <Typography variant="subtitle2" color="text.secondary">
                              Guardian {n} Image
                            </Typography>
                        
                            {imgUrl ? (
                              <Box
                                component="img"
                                src={imgUrl}
                                alt={`Guardian ${n}`}
                                sx={{
                                  width: 140,
                                  height: 160,
                                  borderRadius: 2,
                                  objectFit: "cover",
                                  border: `1px solid ${BORDER}`,
                                  backgroundColor: "rgba(25,118,210,0.04)",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 140,
                                  height: 160,
                                  borderRadius: 2,
                                  border: `1px dashed ${BORDER}`,
                                  backgroundColor: "rgba(25,118,210,0.04)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "text.secondary",
                                }}
                              >
                                No image
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReportStudentPersonalInformation;
